/**
 * Feature Flags Utility
 * Manages feature toggles for Memara UI
 */

export interface FeatureFlags {
  realityBasedOnboarding: boolean;
  // Add more feature flags here as needed
}

/**
 * Get current feature flags configuration
 */
export function getFeatureFlags(): FeatureFlags {
  return {
    realityBasedOnboarding: 
      import.meta.env.VITE_ENABLE_REALITY_ONBOARDING === 'true' || 
      localStorage.getItem('memara_enable_reality_onboarding') === 'true',
  };
}

/**
 * Enable reality-based onboarding flow
 * Usage: enableRealityOnboarding() in browser console
 */
export function enableRealityOnboarding(): void {
  localStorage.setItem('memara_enable_reality_onboarding', 'true');
  console.log('✅ Reality-based onboarding enabled! Refresh the page to see changes.');
  console.log('💡 To disable: disableRealityOnboarding()');
}

/**
 * Disable reality-based onboarding flow
 * Usage: disableRealityOnboarding() in browser console
 */
export function disableRealityOnboarding(): void {
  localStorage.removeItem('memara_enable_reality_onboarding');
  console.log('❌ Reality-based onboarding disabled! Refresh the page to see changes.');
  console.log('💡 To enable: enableRealityOnboarding()');
}

/**
 * Check if reality-based onboarding is enabled
 */
export function isRealityOnboardingEnabled(): boolean {
  return getFeatureFlags().realityBasedOnboarding;
}

const MEMORY_MAP_LS = 'memara_enable_memory_map';

/**
 * Local / dev escape hatch when PostHog is off or flags are not yet loaded.
 * See PostHog flag `memory_map` for production rollout.
 */
export function isMemoryMapLocalOverrideEnabled(): boolean {
  if (import.meta.env.DEV) return true;
  if (import.meta.env.VITE_ENABLE_MEMORY_MAP === 'true') return true;
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(MEMORY_MAP_LS) === 'true';
  } catch {
    return false;
  }
}

/** Console: enableMemoryMap() then refresh. */
export function enableMemoryMap(): void {
  localStorage.setItem(MEMORY_MAP_LS, 'true');
  console.log('Memory Map local override enabled. Refresh the page.');
  console.log('To disable: disableMemoryMap()');
}

/** Console: disableMemoryMap() then refresh. */
export function disableMemoryMap(): void {
  localStorage.removeItem(MEMORY_MAP_LS);
  console.log('Memory Map local override disabled. Refresh the page.');
}

// Make functions available globally for easy console access
if (typeof window !== 'undefined') {
  (window as any).enableRealityOnboarding = enableRealityOnboarding;
  (window as any).disableRealityOnboarding = disableRealityOnboarding;
  (window as any).isRealityOnboardingEnabled = isRealityOnboardingEnabled;
  (window as any).enableMemoryMap = enableMemoryMap;
  (window as any).disableMemoryMap = disableMemoryMap;
} 